import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

import { StandardAlert } from '@/components/ui/alert';
import { Paragraph, SmallText } from '@/components/ui/typography';

interface SectionErrorBoundaryProps {
  sectionId: string;
  sectionTemplateCode: string;
  children: ReactNode;
}

interface SectionErrorBoundaryState {
  hasError: boolean;
}

export class SectionErrorBoundary extends Component<
  SectionErrorBoundaryProps,
  SectionErrorBoundaryState
> {
  override state: SectionErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): SectionErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Page section failed to render', {
      sectionId: this.props.sectionId,
      sectionTemplateCode: this.props.sectionTemplateCode,
      error,
      errorInfo,
    });
  }

  override componentDidUpdate(prevProps: SectionErrorBoundaryProps) {
    if (
      prevProps.sectionId !== this.props.sectionId ||
      prevProps.sectionTemplateCode !== this.props.sectionTemplateCode
    ) {
      if (this.state.hasError) {
        this.setState({ hasError: false });
      }
    }
  }

  override render() {
    if (this.state.hasError) {
      return (
        <StandardAlert
          variant="destructive"
          title="Unable to render section"
          description={
            <div className="space-y-2">
              <Paragraph className="text-destructive">
                Something went wrong while rendering this page section.
              </Paragraph>
              <SmallText className="text-muted-foreground">
                Please refresh the page or contact support if the issue persists.
              </SmallText>
            </div>
          }
          data-slot="section-error-boundary"
        />
      );
    }

    return this.props.children;
  }
}
